import React, {useState, Fragment, useEffect, useMemo} from 'react';
import {useLocation, useParams, useHistory} from 'react-router-dom';
import {Breadcrumbs} from 'worksource-ui';
import {useLazyQuery, useQuery} from '@apollo/react-hooks';
import {gql} from 'apollo-boost';
import {Spinner, NonIdealState, TextArea} from '@blueprintjs/core';

import QASearchBar from 'components/QASearchBar';
import withMainLayout from 'layouts/main/view';
import {showGQLError} from 'utils/toaster';
import HeaderBar from 'components/header-bar';
import FilePreview from 'components/FilePreview';
import {useCurrentUser} from '../utils/auth';

const QA_DESCRIPTION = `
WorkSource Q&A allows you to query the materials stored in your guides using natural language. Ask a question and our assistant will scan through any text-based materials to find relevant details, summarize the results, and cite documents used in its response.
`;

const GUIDE_QUERY = gql`
  query RAGSearchGuideInfo($clientId: ID!, $guideId: ID!) {
    guides(clientId: $clientId, id: $guideId) {
      name,
    },
  }
`;

const getSearchGuide = ({includePrompt = false} = {}) => (
  gql`
    query RAGSearchGuide($clientId: ID!, $guideId: ID!, $query: String!) {
      ragSearchGuide(
        clientId: $clientId,
        guideId: $guideId,
        query: $query
      ) {
        keywordResults {
          id,
          name,
          url,
          thumbnailUrl,
        }
        embeddings {
          file {
            id,
            name,
            url,
            thumbnailUrl,
          },
          text,
        },
        llmText,
        ${includePrompt ? 'systemPrompt,' : ''}
        ${includePrompt ? 'keywordQuery,' : ''}
      }
    }
  `
);

const SOURCE_PATTERN = /\[File ([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\]/g;

const GuideSearchPage = () => {
  ////
  // Hooks
  //
  const location = useLocation();
  const currentUser = useCurrentUser();
  const history = useHistory();
  const {clientId, guideId} = useParams();
  const queryParams = new URLSearchParams(location.search);
  // Only visible to superusers
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);

  ////
  // GraphQL
  //
  const [fetchResults, {loading, data, called}] = useLazyQuery(
    getSearchGuide({includePrompt: currentUser?.superuser}),
    {
      variables: {clientId, guideId},
      onError: error => showGQLError({error}),
      fetchPolicy: 'network-only',
    },
  );
  const {data: guideData} = useQuery(GUIDE_QUERY, {
    variables: {clientId, guideId},
    onError: error => showGQLError({error}),
  });

  const result = (() => {
    if (loading || !data || !data.ragSearchGuide) return null;
    return data.ragSearchGuide;
  })();

  // Mapping of file ID -> files, implicitly filtering out any files that are
  // returned as relevant embeddings but aren't cited in the LLM's response.
  const fileIdToFile = useMemo(() => {
    if (!result) return null;
    if (!result.llmText) return null;

    // Calculate this mapping to remove duplicate files, as there are many
    // embeddings for each file.
    const matches = Array.from(result.llmText.matchAll(SOURCE_PATTERN)) || [];
    return matches
      .reduce((mapping, match) => {
        const [, uuid] = match;
        const {file} = result.embeddings.find(e => e.file.id === uuid);

        return {
          ...mapping,
          [uuid]: file,
        };
      }, {});
  }, [result]);

  // Convert it into an array that we can use to order the sources.
  const sourceFiles = fileIdToFile
    ? Object.values(fileIdToFile)
    : [];

  const keywordFiles = useMemo(() => {
    if (!result || !result.keywordResults) return [];

    if (result.llmText && fileIdToFile) {
      // Only show the result if it is not listed as a source
      return result.keywordResults.filter(({id}) => !fileIdToFile[id]);
    }
    return result.keywordResults;
  }, [result, fileIdToFile]);

  ////
  // Events
  //
  const onSubmitSearch = (query) => {
    fetchResults({
      variables: {query},
    });
  };

  const onClickFile = ({file}) => {
    history.push(`/clients/${clientId}/guides/${guideId}/files/${file.id}`);
  };

  // Run the query if we're passed the q param
  useEffect(() => {
    if (queryParams.get('q')) {
      onSubmitSearch(queryParams.get('q'));
    }
  }, []);

  ////
  // Render
  //
  const llmText = useMemo(() => {
    if (!result) return null;
    if (!result.llmText) return null;

    return result.llmText.split(SOURCE_PATTERN).map((segment, index) => {
      if (index % 2 === 1) {
        const file = fileIdToFile[segment];
        const fileIndex = sourceFiles.findIndex(({id}) => id === file.id);
        return (
          <span
            key={index}
            className="source-citation"
            onClick={() => onClickFile({file})}
          >
            Source {fileIndex + 1}
          </span>
        );
      }

      return <Fragment key={index}>{segment}</Fragment>;
    });
  }, [result, sourceFiles]);

  const content = (() => {
    if (!called) {
      return (
        <Fragment>
          <NonIdealState
            className="guide-search--intro"
            icon="chat"
            title="WorkSource Q&A"
            description={QA_DESCRIPTION}
          />
        </Fragment>
      );
    }

    if (loading || !result || !sourceFiles) {
      return (
        <Fragment>
          <div className="guide-search--header">
            Results
            <Spinner size={8} />
          </div>
          <div className="guide-search--response">
            <p className="bp3-skeleton">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit.
            </p>
            <p className="bp3-skeleton">
              Nullam hendrerit malesuada nunc, eu tincidunt eros vehicula sit
              amet. Suspendisse potenti duis feugiat magna eu pharetra.
            </p>
            <p className="bp3-skeleton">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit.
            </p>
            <p className="bp3-skeleton">
              Nullam hendrerit malesuada nunc, eu tincidunt eros vehicula sit
              amet. Suspendisse potenti duis feugiat magna eu pharetra.
            </p>
          </div>
        </Fragment>
      );
    }

    const noResults = !result.llmText && result.keywordResults.length === 0;
    if (noResults) {
      return (
        <NonIdealState
          className="guide-search--intro"
          icon="search"
          title="No results found"
          description="We were unable to find any results for the specified query in this guide"
        />
      );
    }

    return (
      <Fragment>
        {result.llmText && (
          <Fragment>
            <div className="guide-search--header" onClick={() => setShowSystemPrompt(true)}>
            Assistant summary
            </div>
            <div className="guide-search--response">
              <p>{llmText}</p>
            </div>
          </Fragment>
        )}

        {showSystemPrompt && result.systemPrompt && (
          <Fragment>
            <div className="guide-search--header">
              System prompt
            </div>
            <div className="guide-search--response">
              <TextArea value={result.systemPrompt} />
            </div>
          </Fragment>
        )}

        {sourceFiles.length > 0 && (
          <Fragment>
            <div className="guide-search--header">Sources</div>
            <div className="guide-search--sources">
              {sourceFiles.map((file, index) => {
                const fileIndex = index + 1;

                return (
                  <div key={file.id} className="guide-search--source">
                    <FilePreview
                      file={file}
                      badge={`Source ${fileIndex}`}
                      onClick={onClickFile}
                    />
                  </div>
                );
              })}
            </div>
          </Fragment>
        )}

        {keywordFiles.length > 0 && (
          <Fragment>
            <div className="guide-search--header">Files containing search text</div>
            <div className="guide-search--sources">
              {result.keywordResults.map((file) => {
                return (
                  <div key={file.id} className="guide-search--source">
                    <FilePreview file={file} onClick={onClickFile} />
                  </div>
                );
              })}
            </div>
          </Fragment>
        )}
      </Fragment>
    );
  })();

  const guideName = guideData?.guides[0]?.name;
  return (
    <Fragment>
      <HeaderBar>
        <Breadcrumbs>
          <Breadcrumbs.Breadcrumb
            href={`/clients/${clientId}/guides`}
            icon="book"
            text="Guides"
          />
          {guideName && (
            <Breadcrumbs.Breadcrumb
              href={`/clients/${clientId}/guides/${guideId}`}
              text={guideName}
            />
          )}
          {!guideName && (
            <Breadcrumbs.Breadcrumb
              className="bp3-skeleton"
              href={`/clients/${clientId}/guides/${guideId}`}
              text="loading"
            />
          )}
          <Breadcrumbs.Breadcrumb
            current
            text="Q&A"
            href="#"
            icon="search"
          />
        </Breadcrumbs>
      </HeaderBar>
      <div className="guide-search--container content-container">
        <QASearchBar
          autoFocus
          showHeader={false}
          initialValue={queryParams.get('q')}
          onSubmit={onSubmitSearch}
        />
        {content}
      </div>
      <div className="spacer" />
      <div className="content-container guide-search--disclaimer">
        <p>
          WorkSource Q&A can make mistakes. Please verify important information
          with the original source material.
        </p>
      </div>
    </Fragment>
  );
};

export default withMainLayout({viewerMenu: true})(GuideSearchPage);
